/**
 * Photo Memories (plan approved 2026-07-26; migrations 538–541).
 *
 * Two surfaces on one page:
 *   • a CONFIG card — scan_limit (with a LIVE cost estimate beside it), top_k,
 *     and min_cluster_size, plus a required change-reason. Saving routes through
 *     QuillActionConfirm with explicit production copy (it changes a live,
 *     member-facing scan budget), and the backend rejects a save with no reason.
 *   • a RUN-HISTORY table — every counter that backs a member-facing progress
 *     claim (uploaded, faces, clusters named/skipped, captions, mentions written,
 *     facts written/rejected), the rejected-candidate count, per-run cost actuals
 *     joined from the metered usage ledger, and a Halt button (gated behind a
 *     production-explicit confirm) for an in-flight run.
 *
 * FLAG-TOLERANT: the feature is gated by FEATURE_PHOTO_MEMORIES and its schema is
 * applied by Grant. Until then every read comes back "not enabled" and the page
 * renders an honest EmptyState — never a crash, never a fabricated value. Costs
 * that the ledger can't yet answer render as "—" (unknown), never as $0.00.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  Divider,
  InputAdornment,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import TuneIcon from '@mui/icons-material/Tune';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import EmptyState from '../../common/EmptyState';
import DataTable, { Column } from '../../common/DataTable';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors, typography } from '../../../theme/designTokens';
import {
  photoScanApi,
  QuillApiError,
  PhotoScanConfig,
  PhotoScanRun,
} from './api';
import { estimatePhotoScanCost, formatEstimateRange, formatUsd } from './costEstimate';

// ── Formatting helpers ──────────────────────────────────────────────────────

/** Coerce a node-pg count (bigint/numeric arrives as a string) to a number. */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

function fmtDate(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

/** A run status is terminal (finished) — Halt is a no-op on it. */
const TERMINAL = new Set(['complete', 'halted', 'failed']);

type StatusColor = 'default' | 'success' | 'warning' | 'error' | 'info';

function statusChip(status?: string | null): { label: string; color: StatusColor } {
  const s = (status || '').toLowerCase();
  if (s === 'complete') return { label: 'Complete', color: 'success' };
  if (s === 'failed') return { label: 'Failed', color: 'error' };
  if (s === 'halted') return { label: 'Halted', color: 'warning' };
  if (!s) return { label: '—', color: 'default' };
  // Any in-flight phase (uploading, detecting_faces, …) reads as active/info.
  return { label: status as string, color: 'info' };
}

// ── Config card ─────────────────────────────────────────────────────────────

interface ConfigFormState {
  scan_limit: string;
  top_k: string;
  min_cluster_size: string;
  reason: string;
}

const ConfigCard: React.FC<{
  config: PhotoScanConfig;
  onSaved: (c: PhotoScanConfig) => void;
}> = ({ config, onSaved }) => {
  const [form, setForm] = useState<ConfigFormState>({
    scan_limit: String(config.scan_limit),
    top_k: String(config.top_k),
    min_cluster_size: String(config.min_cluster_size),
    reason: '',
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof ConfigFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const scanLimitN = num(form.scan_limit);
  const estimate = useMemo(() => estimatePhotoScanCost(scanLimitN), [scanLimitN]);

  // Client-side mirror of the backend CHECK constraints, so the Save button is
  // only enabled for a body the backend will accept (it still re-validates).
  const scanLimitValid = Number.isInteger(scanLimitN) && scanLimitN >= 1 && scanLimitN <= 10000;
  const topKN = num(form.top_k);
  const topKValid = Number.isInteger(topKN) && topKN >= 1 && topKN <= 50;
  const minClusterN = num(form.min_cluster_size);
  const minClusterValid = Number.isInteger(minClusterN) && minClusterN >= 1;
  const reasonValid = form.reason.trim().length > 0;
  const formValid = scanLimitValid && topKValid && minClusterValid && reasonValid;

  const changed =
    scanLimitN !== config.scan_limit ||
    topKN !== config.top_k ||
    minClusterN !== config.min_cluster_size;

  const doSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await photoScanApi.updateConfig({
        scan_limit: scanLimitN,
        top_k: topKN,
        min_cluster_size: minClusterN,
        updated_reason: form.reason.trim(),
      });
      setConfirmOpen(false);
      setForm((f) => ({ ...f, reason: '' }));
      onSaved(saved);
    } catch (e) {
      const msg =
        e instanceof QuillApiError && e.code === 'PHOTO_MEMORIES_NOT_ENABLED'
          ? 'Photo Memories is not enabled yet — this save did not land.'
          : e instanceof Error
          ? e.message
          : 'Failed to save the scan config.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [scanLimitN, topKN, minClusterN, form.reason, onSaved]);

  return (
    <Card variant="outlined" sx={{ borderRadius: '16px', borderColor: colors.gray[200] }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <TuneIcon sx={{ color: colors.deepTeal[500] }} />
          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
            Scan configuration
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
          These values are read live by the engine — raising the scan limit changes what a member's next scan
          costs and how long it runs.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Scan limit (photos)"
              value={form.scan_limit}
              onChange={set('scan_limit')}
              error={form.scan_limit !== '' && !scanLimitValid}
              helperText={
                scanLimitValid ? (
                  <Box component="span" sx={{ color: colors.deepTeal[500] }}>
                    Est. AI cost {formatEstimateRange(estimate)} · ~{estimate.captionedPhotos} captioned
                  </Box>
                ) : (
                  'An integer between 1 and 10,000.'
                )
              }
              inputProps={{ min: 1, max: 10000, step: 1, 'aria-label': 'Scan limit' }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Top-K clusters"
              value={form.top_k}
              onChange={set('top_k')}
              error={form.top_k !== '' && !topKValid}
              helperText={topKValid ? 'People shown for naming.' : '1–50.'}
              inputProps={{ min: 1, max: 50, step: 1, 'aria-label': 'Top-K clusters' }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Min cluster size"
              value={form.min_cluster_size}
              onChange={set('min_cluster_size')}
              error={form.min_cluster_size !== '' && !minClusterValid}
              helperText={minClusterValid ? 'Faces to form a person.' : '≥ 1.'}
              inputProps={{ min: 1, step: 1, 'aria-label': 'Min cluster size' }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason for this change"
              placeholder="Why is the scan config changing? (required)"
              value={form.reason}
              onChange={set('reason')}
              error={form.reason !== '' && !reasonValid}
              helperText="Required — every scan-config change is attributed to you and this reason."
              inputProps={{ 'aria-label': 'Reason for this change' }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2.5 }}>
          <Button
            variant="contained"
            disabled={!formValid || !changed}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Save configuration
          </Button>
          {config.updated_at && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Last changed {fmtDate(config.updated_at)}
              {config.updated_by ? ` by ${config.updated_by}` : ''}
            </Typography>
          )}
        </Box>
      </CardContent>

      <QuillActionConfirm
        open={confirmOpen}
        title="Save scan configuration"
        message={
          <>
            Apply this scan configuration to the <strong>live production</strong> Quill engine? The next scan any
            member starts will use <strong>{scanLimitN.toLocaleString()}</strong> photos, top-K{' '}
            <strong>{topKN}</strong>, min cluster <strong>{minClusterN}</strong>.
          </>
        }
        detail={
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Estimated AI cost per member scan at this limit: <strong>{formatEstimateRange(estimate)}</strong> (mid{' '}
            {formatUsd(estimate.midUsd)}). This is a planning estimate — real spend is metered per run.
          </Typography>
        }
        caption={error || undefined}
        confirmLabel="Save to production"
        pendingLabel="Saving…"
        loading={saving}
        onConfirm={doSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </Card>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

const PhotoMemoriesPage: React.FC = () => {
  const { status } = useQuillHealth();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [config, setConfig] = useState<PhotoScanConfig | null>(null);
  const [runs, setRuns] = useState<PhotoScanRun[]>([]);
  const [notEnabled, setNotEnabled] = useState(false);
  const [costDegraded, setCostDegraded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Halt dialog state.
  const [haltTarget, setHaltTarget] = useState<PhotoScanRun | null>(null);
  const [halting, setHalting] = useState(false);
  const [haltError, setHaltError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, runsRes] = await Promise.all([photoScanApi.getConfig(), photoScanApi.getRuns()]);
      if (!mountedRef.current) return;
      setConfig(cfg.config);
      setRuns(runsRes.runs);
      setCostDegraded(runsRes.costDegraded);
      // The whole feature is off only when BOTH surfaces report not-enabled.
      setNotEnabled(cfg.notEnabled && runsRes.notEnabled);
    } catch (e) {
      if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed to load Photo Memories.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmHalt = useCallback(async () => {
    if (!haltTarget) return;
    setHalting(true);
    setHaltError(null);
    try {
      await photoScanApi.haltRun(haltTarget.id);
      setHaltTarget(null);
      await load();
    } catch (e) {
      const msg =
        e instanceof QuillApiError && e.code === 'RUN_ALREADY_TERMINAL'
          ? 'That run already finished — there was nothing to halt.'
          : e instanceof Error
          ? e.message
          : 'Failed to request a halt.';
      setHaltError(msg);
    } finally {
      setHalting(false);
    }
  }, [haltTarget, load]);

  // ── Run-history columns ────────────────────────────────────────────────
  const columns: Column<PhotoScanRun>[] = [
    {
      id: 'status',
      label: 'Status',
      render: (row) => {
        const c = statusChip(row.status);
        return (
          <Box>
            <Chip label={c.label} size="small" color={c.color} variant={c.color === 'default' ? 'outlined' : 'filled'} />
            {row.halt_requested && !TERMINAL.has((row.status || '').toLowerCase()) && (
              <Typography variant="caption" sx={{ display: 'block', color: colors.warning.dark, mt: 0.5 }}>
                halt requested
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'member_id',
      label: 'Member',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.xs }}>
          {row.member_id || '—'}
        </Typography>
      ),
    },
    {
      id: 'progress',
      label: 'Photos',
      align: 'right',
      render: (row) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: typography.sizes.sm }}>
            {num(row.uploaded_count).toLocaleString()} / {num(row.photos_total).toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {num(row.gated_out_count).toLocaleString()} gated out
          </Typography>
        </Box>
      ),
    },
    {
      id: 'people',
      label: 'People',
      align: 'right',
      render: (row) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: typography.sizes.sm }}>
            {num(row.named_count).toLocaleString()} named · {num(row.skipped_count).toLocaleString()} skipped
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {num(row.faces_found).toLocaleString()} faces · {num(row.clusters_count).toLocaleString()} clusters
          </Typography>
        </Box>
      ),
    },
    {
      id: 'memory',
      label: 'Memory written',
      align: 'right',
      render: (row) => (
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: typography.sizes.sm }}>
            {num(row.mentions_written).toLocaleString()} mentions · {num(row.captions_written).toLocaleString()} captions
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {num(row.facts_written).toLocaleString()} facts ·{' '}
            <Box component="span" sx={{ color: num(row.facts_rejected) > 0 ? colors.warning.dark : 'text.disabled' }}>
              {num(row.facts_rejected).toLocaleString()} rejected
            </Box>{' '}
            ({num(row.rejected_candidates_count).toLocaleString()} candidates)
          </Typography>
        </Box>
      ),
    },
    {
      id: 'cost_actual_usd',
      label: 'Cost',
      align: 'right',
      render: (row) =>
        // Unknown (ledger unavailable) reads as "—", never as $0.00.
        row.cost_actual_usd === null || row.cost_actual_usd === undefined ? (
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        ) : (
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: typography.sizes.sm, fontWeight: 600 }}>{formatUsd(num(row.cost_actual_usd))}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {num(row.cost_calls).toLocaleString()} calls
            </Typography>
          </Box>
        ),
    },
    {
      id: 'created_at',
      label: 'Started',
      render: (row) => <Typography sx={{ fontSize: typography.sizes.sm }}>{fmtDate(row.created_at)}</Typography>,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (row) => {
        const terminal = TERMINAL.has((row.status || '').toLowerCase());
        return (
          <Button
            size="small"
            variant="outlined"
            disabled={terminal || row.halt_requested}
            onClick={() => {
              setHaltTarget(row);
              setHaltError(null);
            }}
            sx={{ textTransform: 'none', borderColor: colors.error.light, color: colors.error.main }}
          >
            {row.halt_requested ? 'Halting…' : 'Halt'}
          </Button>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <ImageIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
              Photo Memories
            </Typography>
            <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
              Scan-run history and the live scan configuration for the photo-memory pipeline.
            </Typography>
          </Box>
          <HelpTip id="quill-photo-memories" />
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Quill backend • {status === 'up' ? 'connected' : status}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none' }}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Not-enabled — honest, never a crash. */}
      {!loading && !error && notEnabled && (
        <EmptyState
          icon="image"
          title="Photo Memories is not enabled yet"
          description="The feature is gated by FEATURE_PHOTO_MEMORIES and its database schema (migrations 538–541) has not been applied. This page will populate once the feature is switched on."
        />
      )}

      {!loading && !error && !notEnabled && (
        <>
          {/* Config card */}
          {config ? (
            <Box sx={{ mb: 4 }}>
              <ConfigCard config={config} onSaved={(c) => setConfig(c)} />
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 4 }}>
              The scan-config row could not be read. Run history is still shown below.
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Run history */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
              Recent scan runs
            </Typography>
            {costDegraded && (
              <Chip
                label="cost ledger unavailable"
                size="small"
                variant="outlined"
                sx={{ color: colors.warning.dark, borderColor: colors.warning.light }}
              />
            )}
          </Box>
          <DataTable<PhotoScanRun>
            columns={columns}
            data={runs}
            loading={loading}
            emptyMessage="No photo scans have run yet"
            emptyIcon="image"
          />
        </>
      )}

      {/* Halt confirmation — production-explicit. */}
      <QuillActionConfirm
        open={Boolean(haltTarget)}
        title="Halt this scan run"
        destructive
        message={
          <>
            Request a halt of the <strong>live</strong> scan run for member{' '}
            <strong>{haltTarget?.member_id || 'unknown'}</strong>? The engine stops at its next phase boundary.
            Memory already written by this run is <strong>not</strong> rolled back.
          </>
        }
        detail={
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Current status: <strong>{haltTarget?.status}</strong>. This sets a halt flag the worker checks; it does
            not force-kill the run.
          </Typography>
        }
        caption={haltError || undefined}
        confirmLabel="Request halt"
        pendingLabel="Requesting…"
        loading={halting}
        onConfirm={confirmHalt}
        onCancel={() => setHaltTarget(null)}
      />
    </Box>
  );
};

export default PhotoMemoriesPage;
